import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';
import { 
  CreditCard, Check, Zap, Users, Clock, Calendar, Upload, 
  FileText, IndianRupee, Building, ArrowRight, Shield, Star,
  CheckCircle, XCircle, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionUpgrade() {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [hasExistingSubscription, setHasExistingSubscription] = useState(false);
  const [customRenewalPrice, setCustomRenewalPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [showOfflineForm, setShowOfflineForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [billingCycle, setBillingCycle] = useState('yearly');
  
  // Offline payment form
  const [offlineForm, setOfflineForm] = useState({
    payment_mode: 'bank_transfer',
    transaction_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
    proof: null
  });

  useEffect(() => {
    fetchPlans();
    fetchPaymentRequests();
    
    // Load Razorpay SDK dynamically
    if (!document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/subscription-payments/available-plans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
        setCurrentPlan(data.current_plan);
        setHasExistingSubscription(data.has_existing_subscription || false);
        setCustomRenewalPrice(data.custom_renewal_price || null);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/subscription-payments/my-payment-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentRequests(data.offline_requests || []);
      }
    } catch (error) {
      console.error('Error fetching payment requests:', error);
    }
  };

  const getPrice = (plan) => {
    // For existing subscribers: use custom renewal > plan renewal > first-time
    if (hasExistingSubscription) {
      if (plan.is_current && customRenewalPrice) return customRenewalPrice;
      return plan.renewal_price || plan.first_time_price || plan.prices?.yearly || 0;
    }
    // For new subscribers: use first-time price
    return plan.first_time_price || plan.prices?.yearly || 0;
  };

  // Compute discount amount for a plan at the current billing cycle.
  // Mirrors backend logic: percent of price (rounded) or flat capped at price.
  const getDiscountAmount = (plan, price) => {
    if (!plan?.discount_enabled) return 0;
    const dType = plan.discount_type || 'percent';
    const dVal = parseFloat(
      (billingCycle === 'yearly' ? plan.discount_value_yearly : plan.discount_value_monthly)
      ?? plan.discount_value
      ?? 0
    ) || 0;
    if (dVal <= 0 || price <= 0) return 0;
    if (dType === 'percent') return Math.round(price * (dVal / 100));
    return Math.min(dVal, price);
  };

  const handleOnlinePayment = async (plan) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create order
      const orderResponse = await fetch(`${API_URL}/api/subscription-payments/initiate-online-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_id: plan.id,
          billing_cycle: billingCycle
        })
      });
      
      if (!orderResponse.ok) {
        const error = await orderResponse.json();
        throw new Error(error.detail || 'Failed to create payment order');
      }
      
      const orderData = await orderResponse.json();
      
      // Open Razorpay
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Cloud Campus',
        description: `${plan.display_name || plan.name} - Yearly subscription`,
        order_id: orderData.gateway_order_id,
        prefill: orderData.prefill,
        handler: async function(response) {
          // Verify payment
          try {
            const verifyResponse = await fetch(`${API_URL}/api/subscription-payments/verify-online-payment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                order_id: orderData.order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              })
            });
            
            if (verifyResponse.ok) {
              const result = await verifyResponse.json();
              toast.success(result.message);
              window.location.reload();
            } else {
              const error = await verifyResponse.json();
              toast.error(error.detail || 'Payment verification failed');
            }
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        theme: {
          color: '#3B82F6'
        }
      };
      
      if (!window.Razorpay) {
        toast.error('Payment gateway is loading. Please try again in a moment.');
        setProcessing(false);
        return;
      }
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast.error(error.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleOfflinePayment = async (e) => {
    e.preventDefault();
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('plan_id', selectedPlan.id);
      formData.append('billing_cycle', 'yearly');
      formData.append('amount', getPrice(selectedPlan));
      formData.append('payment_mode', offlineForm.payment_mode);
      formData.append('transaction_id', offlineForm.transaction_id);
      formData.append('payment_date', offlineForm.payment_date);
      formData.append('notes', offlineForm.notes);
      
      if (offlineForm.proof) {
        formData.append('proof', offlineForm.proof);
      }
      
      const response = await fetch(`${API_URL}/api/subscription-payments/submit-offline-payment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowOfflineForm(false);
        fetchPaymentRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit payment request');
      }
    } catch (error) {
      toast.error('Failed to submit payment request');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-upgrade-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Upgrade Your Subscription</h1>
        <p className="opacity-90">Choose a plan that best fits your institution's needs</p>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center bg-white border border-gray-200 rounded-full p-1 shadow-sm" data-testid="billing-cycle-toggle">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition ${
              billingCycle === 'monthly' ? 'bg-[#0a1628] text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="cycle-monthly"
          >Monthly</button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${
              billingCycle === 'yearly' ? 'bg-[#0a1628] text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="cycle-yearly"
          >
            Yearly
            <span className={`text-xs px-1.5 py-0.5 rounded ${billingCycle === 'yearly' ? 'bg-green-500' : 'bg-green-100 text-green-700'}`}>Save</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrent = plan.is_current;
          const price = getPrice(plan);
          const discountAmt = getDiscountAmount(plan, price);
          const afterDiscount = Math.max(0, price - discountAmt);
          const gstPct = (plan.gst_percent ?? 18) || 0;
          const gstAmt = plan.gst_included ? Math.round(afterDiscount - (afterDiscount / (1 + gstPct / 100))) : Math.round(afterDiscount * gstPct / 100);
          const total = plan.gst_included ? afterDiscount : afterDiscount + gstAmt;
          
          return (
            <Card 
              key={plan.id} 
              className={`relative ${isCurrent ? 'border-blue-500 border-2' : ''} ${
                selectedPlan?.id === plan.id ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-blue-500">Current Plan</Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl">{plan.display_name || plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="text-center">
                <div className="mb-4">
                  {discountAmt > 0 && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 mb-2 rounded-full bg-red-50 border border-red-200 text-red-600 text-xs font-semibold">
                      {(plan.discount_type || 'percent') === 'percent'
                        ? `${parseFloat(billingCycle === 'yearly' ? plan.discount_value_yearly : plan.discount_value_monthly) || plan.discount_value}% OFF`
                        : `₹${discountAmt.toLocaleString('en-IN')} OFF`}
                      {plan.discount_label ? ` · ${plan.discount_label}` : ''}
                    </div>
                  )}
                  <div className="flex items-baseline justify-center gap-2">
                    {discountAmt > 0 && <span className="text-lg text-gray-400 line-through">₹{price.toLocaleString('en-IN')}</span>}
                    <span className="text-4xl font-bold">₹{afterDiscount.toLocaleString('en-IN')}</span>
                    <span className="text-gray-500 text-sm">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 space-y-0.5 max-w-xs mx-auto">
                    {discountAmt > 0 && (
                      <div className="flex justify-between text-red-600"><span>Discount</span><span>− ₹{discountAmt.toLocaleString('en-IN')}</span></div>
                    )}
                    {gstPct > 0 && (
                      <div className="flex justify-between">
                        <span>{plan.gst_included ? `Incl. ${gstPct}% GST` : `+ ${gstPct}% GST`}</span>
                        <span>₹{gstAmt.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-gray-100">
                      <span>Total payable</span><span>₹{total.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  {hasExistingSubscription && isCurrent && customRenewalPrice && billingCycle === 'yearly' && (
                    <p className="text-xs text-green-600 mt-1">Custom renewal price applied</p>
                  )}
                </div>
                
                <div className="space-y-2 text-left text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-500" />
                    <span>Up to {plan.max_students || '∞'} Students</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-green-500" />
                    <span>Up to {plan.max_staff || '∞'} Staff</span>
                  </div>
                  {(plan.features || []).slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col gap-2">
                {!isCurrent && (
                  <>
                    <Button 
                      className="w-full" 
                      onClick={() => handleOnlinePayment(plan)}
                      disabled={processing}
                    >
                      {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                      Pay Online
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        setSelectedPlan(plan);
                        setShowOfflineForm(true);
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Offline Payment
                    </Button>
                  </>
                )}
                {isCurrent && (
                  <Button variant="outline" disabled className="w-full">
                    <Check className="h-4 w-4 mr-2" />
                    Current Plan
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Payment History */}
      {paymentRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Your Payment Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {paymentRequests.map((request) => (
                <div key={request.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{request.plan_name}</p>
                    <p className="text-sm text-gray-500">
                      ₹{request.amount?.toLocaleString('en-IN')} • {request.payment_mode} • {formatDate(request.submitted_at)}
                    </p>
                  </div>
                  <Badge variant={
                    request.status === 'approved' ? 'success' :
                    request.status === 'rejected' ? 'destructive' : 'secondary'
                  }>
                    {request.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                    {request.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                    {request.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Offline Payment Dialog */}
      <Dialog open={showOfflineForm} onOpenChange={setShowOfflineForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Submit Offline Payment</DialogTitle>
            <DialogDescription>
              Fill in your payment details. Our team will verify and activate your subscription.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleOfflinePayment} className="space-y-4">
            {selectedPlan && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="font-medium">{selectedPlan.display_name || selectedPlan.name}</p>
                <p className="text-sm text-gray-600">
                  ₹{getPrice(selectedPlan).toLocaleString('en-IN')} / year
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select 
                value={offlineForm.payment_mode} 
                onValueChange={(v) => setOfflineForm({...offlineForm, payment_mode: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer (NEFT/RTGS/IMPS)</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Transaction ID / UTR Number</Label>
              <Input 
                value={offlineForm.transaction_id}
                onChange={(e) => setOfflineForm({...offlineForm, transaction_id: e.target.value})}
                placeholder="Enter transaction reference"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input 
                type="date"
                value={offlineForm.payment_date}
                onChange={(e) => setOfflineForm({...offlineForm, payment_date: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Upload Payment Proof (Optional)</Label>
              <Input 
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setOfflineForm({...offlineForm, proof: e.target.files[0]})}
              />
              <p className="text-xs text-gray-500">Screenshot, receipt, or bank statement</p>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={offlineForm.notes}
                onChange={(e) => setOfflineForm({...offlineForm, notes: e.target.value})}
                placeholder="Any additional information"
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowOfflineForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

